import type { ReportQueryResponse } from '@/api/types';

export const PERF_HALO_METRIC_IDS = [
  'gross_spend',
  'impressions',
  'clicks',
  'ctr',
  'cpm',
  'cpc',
  'attributed_conversions',
  'attributed_revenue',
  'cpa',
  'roas',
] as const;

export type PerfHaloMetricId = typeof PERF_HALO_METRIC_IDS[number];
export type PerfHaloSortField = PerfHaloMetricId;
export type PerfHaloSortDirection = 'asc' | 'desc';
export type PerfHaloEffect = 'total' | 'direct' | 'halo';

/**
 * The presentation boundary for Perf + Halo.
 *
 * Provider-attributed counts and values remain in micros until formatting.
 * The aggregate aliases are intentionally retained as the authoritative
 * totals; `perfHaloIntegrityError` verifies their exact additivity before the
 * screen may render or export them.
 */
export interface PerfHaloMetrics {
  gross_spend_cents: number;
  /** Gross-spend denominator aligned to the attribution publication clock. */
  attribution_gross_spend_cents: number | null;
  impressions: number;
  clicks: number;
  total_tickets_micros: number;
  direct_tickets_micros: number;
  halo_tickets_micros: number;
  total_ticket_value_micros: number;
  direct_ticket_value_micros: number;
  halo_ticket_value_micros: number;
}

export interface PerfHaloBreakdown {
  total: number;
  direct: number;
  halo: number;
  haloShare: number | null;
}

type ReportMetricSet = ReportQueryResponse['totals'];

function finite(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function finiteOptional(value: unknown): number | null {
  const number = Number(value);
  return value != null && Number.isFinite(number) ? number : null;
}

export function projectPerfHaloMetrics(
  metrics: ReportMetricSet,
): PerfHaloMetrics {
  return {
    gross_spend_cents: finite(metrics.billable_spend_cents),
    attribution_gross_spend_cents: finiteOptional(
      metrics.attribution_billable_spend_cents,
    ),
    impressions: finite(metrics.impressions),
    clicks: finite(metrics.clicks),
    total_tickets_micros: finite(metrics.attributed_conversions_micros),
    direct_tickets_micros: finite(metrics.assigned_conversions_micros),
    halo_tickets_micros: finite(metrics.halo_conversions_micros),
    total_ticket_value_micros: finite(metrics.attributed_revenue_micros),
    direct_ticket_value_micros: finite(metrics.assigned_revenue_micros),
    halo_ticket_value_micros: finite(metrics.halo_revenue_micros),
  };
}

export function emptyPerfHaloMetrics(): PerfHaloMetrics {
  return {
    gross_spend_cents: 0,
    attribution_gross_spend_cents: null,
    impressions: 0,
    clicks: 0,
    total_tickets_micros: 0,
    direct_tickets_micros: 0,
    halo_tickets_micros: 0,
    total_ticket_value_micros: 0,
    direct_ticket_value_micros: 0,
    halo_ticket_value_micros: 0,
  };
}

export function sumPerfHaloMetrics(
  rows: readonly PerfHaloMetrics[],
): PerfHaloMetrics {
  const total = rows.reduce<PerfHaloMetrics>((sum, row) => ({
    gross_spend_cents: sum.gross_spend_cents + row.gross_spend_cents,
    attribution_gross_spend_cents: null,
    impressions: sum.impressions + row.impressions,
    clicks: sum.clicks + row.clicks,
    total_tickets_micros: sum.total_tickets_micros + row.total_tickets_micros,
    direct_tickets_micros: sum.direct_tickets_micros + row.direct_tickets_micros,
    halo_tickets_micros: sum.halo_tickets_micros + row.halo_tickets_micros,
    total_ticket_value_micros:
      sum.total_ticket_value_micros + row.total_ticket_value_micros,
    direct_ticket_value_micros:
      sum.direct_ticket_value_micros + row.direct_ticket_value_micros,
    halo_ticket_value_micros:
      sum.halo_ticket_value_micros + row.halo_ticket_value_micros,
  }), emptyPerfHaloMetrics());
  total.attribution_gross_spend_cents = rows.every(
    (row) => row.attribution_gross_spend_cents !== null,
  )
    ? rows.reduce((sum, row) => sum + row.attribution_gross_spend_cents!, 0)
    : null;
  return total;
}

export function perfHaloGrossSpendDollars(metrics: PerfHaloMetrics): number {
  return metrics.gross_spend_cents / 100;
}

export function perfHaloAttributionGrossSpendDollars(
  metrics: PerfHaloMetrics,
): number | null {
  return metrics.attribution_gross_spend_cents == null
    ? null
    : metrics.attribution_gross_spend_cents / 100;
}

export function perfHaloTickets(
  metrics: PerfHaloMetrics,
  effect: PerfHaloEffect = 'total',
): number {
  const micros = effect === 'direct'
    ? metrics.direct_tickets_micros
    : effect === 'halo'
      ? metrics.halo_tickets_micros
      : metrics.total_tickets_micros;
  return micros / 1_000_000;
}

export function perfHaloTicketValueDollars(
  metrics: PerfHaloMetrics,
  effect: PerfHaloEffect = 'total',
): number {
  const micros = effect === 'direct'
    ? metrics.direct_ticket_value_micros
    : effect === 'halo'
      ? metrics.halo_ticket_value_micros
      : metrics.total_ticket_value_micros;
  return micros / 1_000_000;
}

/** Gross-spend ROAS. Direct and Halo are additive contributions to Total. */
export function perfHaloRoas(
  metrics: PerfHaloMetrics,
  effect: PerfHaloEffect = 'total',
): number | null {
  const spendDollars = perfHaloAttributionGrossSpendDollars(metrics);
  return spendDollars !== null && spendDollars > 0
    ? perfHaloTicketValueDollars(metrics, effect) / spendDollars
    : null;
}

function ratio(
  numerator: number,
  denominator: number,
  multiplier = 1,
): number | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return null;
  }
  return numerator / denominator * multiplier;
}

/**
 * Returns the primary table/KPI value. Delivery is never split by attribution;
 * CPA is deliberately total-only. `effect` changes only Tickets, Ticket Value,
 * and ROAS.
 */
export function perfHaloMetricValue(
  metrics: PerfHaloMetrics,
  metric: PerfHaloMetricId,
  effect: PerfHaloEffect = 'total',
): number | null {
  const spendDollars = perfHaloGrossSpendDollars(metrics);
  const attributionSpendDollars = perfHaloAttributionGrossSpendDollars(metrics);
  const totalTickets = perfHaloTickets(metrics);
  switch (metric) {
    case 'gross_spend':
      return spendDollars;
    case 'impressions':
      return metrics.impressions;
    case 'clicks':
      return metrics.clicks;
    case 'ctr':
      return ratio(metrics.clicks, metrics.impressions, 100);
    case 'cpm':
      return ratio(spendDollars, metrics.impressions, 1_000);
    case 'cpc':
      return ratio(spendDollars, metrics.clicks);
    case 'attributed_conversions':
      return perfHaloTickets(metrics, effect);
    case 'attributed_revenue':
      return perfHaloTicketValueDollars(metrics, effect);
    case 'cpa':
      return attributionSpendDollars === null
        ? null
        : ratio(attributionSpendDollars, totalTickets);
    case 'roas':
      return perfHaloRoas(metrics, effect);
  }
}

export function perfHaloBreakdown(
  metrics: PerfHaloMetrics,
  metric: 'attributed_conversions' | 'attributed_revenue' | 'roas',
): PerfHaloBreakdown {
  const total = perfHaloMetricValue(metrics, metric, 'total') ?? 0;
  const direct = perfHaloMetricValue(metrics, metric, 'direct') ?? 0;
  const halo = perfHaloMetricValue(metrics, metric, 'halo') ?? 0;
  return {
    total,
    direct,
    halo,
    // Signed provider corrections are intentionally not normalized away.
    haloShare: total === 0 ? null : halo / total,
  };
}

/**
 * Numeric values sort by the primary Total metric. Undefined ratios are always
 * last, and equal values use an invariant identity tie-breaker so pagination
 * and refetches cannot reshuffle the table.
 */
export function comparePerfHaloMetrics(
  left: PerfHaloMetrics,
  right: PerfHaloMetrics,
  field: PerfHaloSortField,
  direction: PerfHaloSortDirection,
  leftIdentity = '',
  rightIdentity = '',
): number {
  const leftValue = perfHaloMetricValue(left, field);
  const rightValue = perfHaloMetricValue(right, field);
  if (leftValue === null && rightValue !== null) return 1;
  if (rightValue === null && leftValue !== null) return -1;
  if (leftValue !== null && rightValue !== null && leftValue !== rightValue) {
    return direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
  }
  return leftIdentity.localeCompare(rightIdentity);
}

export function hasPerfHaloDeliveryEvidence(metrics: PerfHaloMetrics): boolean {
  return metrics.gross_spend_cents !== 0
    || metrics.impressions !== 0
    || metrics.clicks !== 0;
}

export function hasPerfHaloAttributionEvidence(metrics: PerfHaloMetrics): boolean {
  return metrics.total_tickets_micros !== 0
    || metrics.direct_tickets_micros !== 0
    || metrics.halo_tickets_micros !== 0
    || metrics.total_ticket_value_micros !== 0
    || metrics.direct_ticket_value_micros !== 0
    || metrics.halo_ticket_value_micros !== 0;
}
